<template>
    <div
        class="bg-white/50 hover:bg-white/70 transition shadow-sm rounded-lg"
        :class="{ 'animate-inbox-blink': props.highlighted }"
    >
        <button
            type="button"
            class="flex w-full items-center gap-2 px-4 py-2.5 text-start bg-transparent border-0 cursor-pointer rounded-lg"
            data-test="category-toggle"
            @click="toggle"
        >
            <span
                class="inline-block size-[11px] rounded-[3px] shrink-0"
                :style="{ backgroundColor: '#' + props.category.color }"
            ></span>
            <span class="flex-1 truncate text-sm text-slate-600">{{ props.category.name }}</span>
            <vx-icon-loading v-if="props.loadingCounts" class="w-2.5 h-2.5" spin />
            <vx-badge v-else-if="props.count > 0" color="primary" class="min-w-5 text-center" size="2xs">
                {{ props.count }}
            </vx-badge>
            <font-awesome-icon
                widthAuto
                class="text-xs text-slate-400 transition-transform"
                :class="{ 'rotate-180': active }"
                :icon="faChevronDown"
            ></font-awesome-icon>
        </button>
        <div v-if="active" class="px-2 pb-2 flex flex-col gap-1.5">
            <template v-if="error">
                <vx-alert color="danger" :closeable="false">
                    {{ error }}

                    <template v-slot:right>
                        <vx-button
                            @click="fetchThreads"
                            data-test="retry-button"
                            type="button"
                            size="sm"
                            color="danger"
                        >
                            Retry
                            <font-awesome-icon :icon="faRefresh"></font-awesome-icon>
                        </vx-button>
                    </template>
                </vx-alert>
            </template>
            <template v-else>
                <message-hub-thread-sidebar-row
                    v-for="thread in threads"
                    :key="`${thread.group_id}-${thread.contact_id}`"
                    :thread="thread"
                ></message-hub-thread-sidebar-row>

                <div
                    v-if="loading && threads.length === 0"
                    data-test="threads-loading-skeleton"
                    class="flex flex-col gap-1.5"
                >
                    <div
                        v-for="n in 3"
                        :key="n"
                        :style="{ animationDelay: `${n * 0.15}s` }"
                        class="rounded-lg animate-pulse bg-slate-200 h-[60px] w-full"
                    ></div>
                </div>

                <vx-button
                    v-if="hasMore"
                    size="sm"
                    color="muted"
                    class="w-full mt-1"
                    :loading="loading"
                    @click="fetchThreads"
                >
                    Load More
                </vx-button>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { faChevronDown, faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxBadge, VxButton, VxIconLoading } from '@voxie/frontend-components';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import Bus from '~/globals/eventBus';
import MessageHubThreadSidebarRow from '~/components/message-hub/message-hub-threads/MessageHubThreadSidebarRow.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { threadsClient } from '~/services';
import type { Thread, ThreadsGetParams } from '~/services/threads/threadsClient.types';
import type { ThreadCategoryType } from '~/services/threadCategories/threadCategoriesClient.types';
import type { ThreadSectionValue } from '~/constants/messageHub';
import { filtersToParams, isSameThread } from '~/components/message-hub/messageHubUtil';
import { useIntelligentInbox } from './useIntelligentInbox';

const PAGE_LIMIT = 20;

const props = defineProps<{
    category: ThreadCategoryType;
    sectionValue: ThreadSectionValue;
    loadingCounts: boolean;
    count: number;
    highlighted: boolean;
    autoExpand?: boolean;
}>();

const { filters, isDefaultView, inboxContext, sortForward, isCategoryActive, setCategoryActive } =
    useIntelligentInbox();
const teamId = Spark.state.currentTeam.id;

const active = computed<boolean>({
    get: () => isCategoryActive(props.sectionValue, props.category.id),
    set: (value: boolean) => setCategoryActive(props.sectionValue, props.category.id, value),
});
const fetched = ref(false);
const lastPageSize = ref(0);
const {
    list: threads,
    loading,
    error,
    fetchParams,
    fetchRecords,
    isLast,
} = useCursorPagination<Thread>({
    suppressToast: true,
});

const hasMore = computed(() => !isLast.value && lastPageSize.value >= PAGE_LIMIT);

const fetchThreads = async (): Promise<void> => {
    const params: ThreadsGetParams = {
        ...filtersToParams(filters.value, teamId, { includeLifecycleMembershipFilters: true }),
        thread_section: props.sectionValue,
        thread_category_id: props.category.id ?? 0,
        cursor: fetchParams.value.cursor ?? undefined,
        forward: sortForward.value ? 'true' : 'false',
        limit: PAGE_LIMIT,
    };

    const newThreads = await fetchRecords(threadsClient.getThreads(inboxContext.value, params));

    if (newThreads) {
        fetched.value = true;
        lastPageSize.value = newThreads.length;
    }
};

const reload = (): Promise<void> => {
    fetchParams.value = { cursor: undefined, forward: sortForward.value };
    lastPageSize.value = 0;
    fetched.value = false;
    return fetchThreads();
};

const toggle = (): void => {
    active.value = !active.value;
    if (!active.value) {
        return;
    }
    reload();
};

watch(
    () => props.autoExpand,
    (expand) => {
        if (expand && !active.value && !fetched.value) {
            toggle();
        }
    },
    { immediate: true }
);

watch(
    [filters, sortForward, inboxContext],
    () => {
        if (!active.value) {
            return;
        }
        reload();
    },
    { deep: true }
);

const belongsToMe = (thread: { thread_section?: string; thread_category_id?: number | null }): boolean =>
    thread.thread_section === props.sectionValue && (thread.thread_category_id ?? null) === (props.category.id ?? null);

const handleThreadUpdated = ({ thread, isInteraction = true }: { thread: Thread; isInteraction?: boolean }): void => {
    if (!fetched.value) {
        return;
    }

    const existingIndex = threads.value.findIndex((existing) => isSameThread(existing, thread));

    if (existingIndex !== -1) {
        if (belongsToMe(thread)) {
            threads.value[existingIndex] = { ...threads.value[existingIndex], ...thread };
        } else {
            threads.value.splice(existingIndex, 1);
            // Thread is leaving this section/category. Re-emit without the flag so the
            // destination pulls it via its normal insert path instead of vanishing.
            if (!isInteraction) {
                Bus.$emit('threadUpdated', { thread });
            }
        }
        return;
    }

    // When only the section/category changed, don't pull a thread the user hasn't loaded into view.
    if (!isInteraction) {
        return;
    }

    if (belongsToMe(thread) && isDefaultView.value) {
        threads.value.unshift(thread);
    }
};

onMounted(() => {
    Bus.$on('threadUpdated', handleThreadUpdated);

    if (active.value && !fetched.value && !loading.value) {
        reload();
    }
});

onUnmounted(() => {
    Bus.$off('threadUpdated', handleThreadUpdated);
});
</script>
